import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CalendarCheck, PhoneCall, Users2, Info, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, formatDate, formatDateTime } from "@/lib/api";
import { NotTaken, AdminHolidays } from "@/pages/admin/AttendanceNotTaken";

const Stat = ({ label, value, color }) => (
  <div className="text-center"><p className="text-[11px] text-slate-300">{label}</p><p className={`font-extrabold text-lg ${color}`}>{value}</p></div>
);

function Overview() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [klass, setKlass] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      if (klass !== "all") params.klass = klass;
      const res = await api.get("/admin/student-attendance/overview", { params });
      setData(res.data);
    } catch { toast.error("Could not load attendance"); }
    finally { setLoading(false); }
  }, [date, klass]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-att-overview">
      <div className="flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">Date (IST)</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="admin-att-date" /></div>
        <div><Label className="text-xs">Class / Batch</Label>
          <Select value={klass} onValueChange={setKlass}>
            <SelectTrigger className="w-48" data-testid="admin-att-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="admin-att-class-all">All classes</SelectItem>
              {(data?.classes || []).map((c) => <SelectItem key={c} value={c} data-testid={`admin-att-class-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
        : !data || data.batches.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="admin-att-empty"><Info className="h-7 w-7 mx-auto mb-2 text-slate-400" />No attendance recorded for this day.</div>
        : (
        <>
          <div className="rounded-2xl bg-slate-900 text-white p-4 grid grid-cols-4 gap-2" data-testid="admin-att-totals">
            <Stat label="Total" value={data.totals.total} color="text-white" />
            <Stat label="Present" value={data.totals.present} color="text-green-400" />
            <Stat label="Informed" value={data.totals.informed_absent} color="text-amber-300" />
            <Stat label="Uninformed" value={data.totals.uninformed_absent} color="text-red-400" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {data.batches.map((b, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-att-batch-${b.klass}`}>
                <div className="flex items-center justify-between">
                  <p className="font-bold text-slate-900">{b.klass}</p>
                  <span className="text-xs text-slate-500">{b.teacher_name}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Present {b.totals.present} · Informed {b.totals.informed_absent} · Uninformed {b.totals.uninformed_absent} (of {b.totals.total})</p>
                {b.absentees.length === 0 ? <p className="text-xs text-green-600 mt-2">All present.</p> : (
                  <ul className="mt-2 space-y-1">
                    {b.absentees.map((a) => (
                      <li key={a.student_id} className="text-sm text-slate-700 flex items-center gap-2">
                        <Badge className={a.absence_type === "informed" ? "bg-amber-500" : "bg-red-600"}>{a.absence_type === "informed" ? "Informed" : "Uninformed"}</Badge>
                        {a.name} <span className="text-slate-400 text-xs">· {a.roll_number}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Followups() {
  const [status, setStatus] = useState("open");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/student-attendance/followups", { params: { status } });
      setList(data);
    } catch { toast.error("Could not load follow-ups"); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const fmt = formatDateTime;

  return (
    <div className="space-y-4" data-testid="admin-att-followups">
      <div className="flex gap-2">
        {[["open", "Open"], ["resolved", "Resolved"], ["", "All"]].map(([v, l]) => (
          <Button key={v} size="sm" variant={status === v ? "default" : "outline"} className={status === v ? "bg-blue-900" : ""} onClick={() => setStatus(v)} data-testid={`admin-followup-filter-${v || "all"}`}>{l}</Button>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
        : list.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="admin-followup-empty"><Info className="h-7 w-7 mx-auto mb-2 text-slate-400" />No follow-ups.</div>
        : (
        <div className="space-y-3">
          {list.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-followup-${f.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-bold text-slate-900">{f.student_name} <span className="text-slate-400 font-normal text-sm">· {f.klass} · Roll {f.roll_number}</span></p>
                  <p className="text-xs text-slate-500">Teacher: {f.teacher_name} · Last absent {f.last_absent_date ? formatDate(f.last_absent_date) : "—"}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className="bg-red-600">{f.streak} days</Badge>
                  {f.resolved ? <Badge className="bg-green-600">Resolved</Badge>
                    : f.needs_meeting ? <Badge className="bg-red-900"><Users2 className="h-3 w-3 mr-1" />Meeting</Badge>
                    : <Badge className="bg-amber-500"><PhoneCall className="h-3 w-3 mr-1" />Call</Badge>}
                </div>
              </div>
              {(f.contact_log || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {f.contact_log.map((c, i) => (
                    <div key={i} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-2"><span className="font-semibold capitalize">{c.action}</span> — {c.note} <span className="text-slate-400">· {c.by} · {fmt(c.at)}{c.status === "resolved" ? " · resolved" : ""}</span></div>
                  ))}
                </div>
              )}
              {(f.contact_log || []).length === 0 && <p className="text-xs text-slate-400 mt-2">No parent contact logged yet.</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailLog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/student-attendance/email-log"); setData(data); }
    catch { toast.error("Could not load email log"); }
    finally { setLoading(false); }
  })(); }, []);
  const fmt = formatDateTime;
  return (
    <div className="space-y-3" data-testid="admin-att-emaillog">
      <div className={`rounded-lg px-3 py-2 text-xs ${data?.live_email_enabled ? "bg-green-50 border border-green-200 text-green-800" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
        {data?.live_email_enabled ? "Live email is ON — parents/admin receive these emails." : "TEST MODE — emails are logged here but NOT sent. Enable live email to start sending."}
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
        : !data || data.logs.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500"><Info className="h-7 w-7 mx-auto mb-2 text-slate-400" />No emails logged yet.</div>
        : (
        <div className="space-y-2">
          {data.logs.map((l) => (
            <div key={l.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm flex items-center gap-3" data-testid={`admin-email-${l.id}`}>
              <Mail className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{l.subject}</p>
                <p className="text-xs text-slate-500 truncate">{l.kind} · to {l.to || "—"} · {fmt(l.created_at)}</p>
              </div>
              <Badge className={l.sent ? "bg-green-600" : "bg-slate-400"}>{l.mode === "live" && l.sent ? "sent" : "test"}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminStudentAttendance() {
  return (
    <div data-testid="panel-student-attendance">
      <h1 className="text-2xl font-extrabold text-slate-900 mb-1 flex items-center gap-2"><CalendarCheck className="h-6 w-6 text-blue-900" /> Student Attendance</h1>
      <p className="text-sm text-slate-500 mb-5">Daily class-wise attendance, consecutive-absence follow-ups and parent-email log. A consolidated summary is emailed to admin at 9 PM IST (TEST mode). Times are IST.</p>
      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-att-tab-overview">Daily Overview</TabsTrigger>
          <TabsTrigger value="notaken" data-testid="admin-att-tab-notaken">Not Taken</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="admin-att-tab-holidays">Holidays</TabsTrigger>
          <TabsTrigger value="followups" data-testid="admin-att-tab-followups">Follow-ups</TabsTrigger>
          <TabsTrigger value="emails" data-testid="admin-att-tab-emails">Email Log</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="notaken"><NotTaken /></TabsContent>
        <TabsContent value="holidays"><AdminHolidays /></TabsContent>
        <TabsContent value="followups"><Followups /></TabsContent>
        <TabsContent value="emails"><EmailLog /></TabsContent>
      </Tabs>
    </div>
  );
}
